import React, { ReactNode } from "react"
import styled from "styled-components"

import { COLORS, BREAKPOINTS, SIZES } from "src/constants"
import { IArticle } from "src/types"
import { useCart } from "src/components/cart"
import { formatEventDate } from "src/helpers/date"
import { BookSection, Book, BookTitle, BookSubtitle } from "src/components/styled"
import CartIcon from "src/svg/Cart"
import { formatMoney } from "src/helpers/text"
import { MAIL_PRICE } from "src/constants"

export const Content = styled.div`
  @media (min-width: ${BREAKPOINTS.tablet}px) {
    margin: 0 90px 60px;
  }
  p {
    text-align: center;
    margin: 15vh 0 20vh;
  }
`

export const Nav = styled.div`
  font-weight: bold;
  .active {
    color: ${COLORS.main};
  }
`
interface CrumbProps {
  active: string
}
const BREADCRUMBS = ["panier", "informations", "paiement", "confirmation"]
export const Breadcrumbs: React.FC<CrumbProps> = ({ active }) => (
  <Nav>
    {BREADCRUMBS.map((crumb, index) => (
      <React.Fragment key={index}>
        {index > 0 && " > "}
        <span className={active === crumb ? "active" : null}>{crumb}</span>
      </React.Fragment>
    ))}
  </Nav>
)

export const ShopTitle: React.FC = () => (
  <BookSection>
    <CartIcon />
    <Book>
      <BookTitle>Ma commande</BookTitle>
      <BookSubtitle>Ateliers Chutes Libres</BookSubtitle>
    </Book>
  </BookSection>
)

export const StepTitle = styled.h2`
  margin-top: 40px;
  color: ${COLORS.main};
  font-size: ${SIZES.medium};
  @media (max-width: ${BREAKPOINTS.tablet}px) {
    font-size: ${SIZES.normal};
  }
`

const Field = styled.div`
  margin-bottom: 20px;
  span {
    width: 120px;
    margin: 0 10px 0 -40px;
    display: inline-block;
    font-weight: bold;
    text-align: right;
  }
  select {
    border: 1px solid ${COLORS.dark};
    background-color: transparent;
    border-radius: 0;
    padding: 5px 25px 5px 8px;
    appearance: none;
    background-image: url(data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAxNjAgMjEwIiB3aWR0aD0iMjQiPiAgICA8cG9seWdvbiBwb2ludHM9IjE2MCAxMzAgMTQ1LjkgMTE1LjkgOTAgMTcxLjcgOTAgNTAgNzAgNTAgNzAgMTcxLjcgMTQuMiAxMTUuOCAwIDEzMCA4MCAyMTAiIC8+ICAgIDxwb2x5Z29uIHBvaW50cz0iMCA4MCAxNC4xIDk0LjEgNzAgMzguMyA3MCAxNjAgOTAgMTYwIDkwIDM4LjMgMTQ1LjggOTQuMiAxNjAgODAgODAgMCIgLz48L3N2Zz4=);
    background-position: right center;
    background-repeat: no-repeat;
    font-weight: bold;
  }
  p {
    margin: 10px 0 10px 90px;
  }
  select,
  p {
    width: 100%;
    max-width: ${BREAKPOINTS.maxTextWidth - 90}px;
  }
  .small {
    max-width: 90px;
  }
  label {
    cursor: pointer;
    display: inline-flex;
    vertical-align: -8px;
  }
  input[type="checkbox"] {
    cursor: pointer;
    flex-shrink: 0;
    appearance: none;
    width: 24px;
    height: 24px;
    border: 1px solid ${COLORS.dark};
    :checked {
      background-image: url(data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgd2lkdGg9IjI0Ij48cGF0aCBkPSJNMCAwaDI0djI0SDB6IiBmaWxsPSJub25lIi8+PHBhdGggZD0iTTkgMTYuMTdMNC44MyAxMmwtMS40MiAxLjQxTDkgMTkgMjEgN2wtMS40MS0xLjQxeiIvPjwvc3ZnPg==);
      background-position: center center;
    }
    margin-right: 12px;
  }

  @media (max-width: ${BREAKPOINTS.tablet}px) {
    p {
      margin-left: 0;
    }
    span {
      display: block;
      width: 100%;
      text-align: left;
      margin: 5px 0;
    }
  }
`

interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label: string
  description?: ReactNode
}

export const Select: React.FC<SelectProps> = ({ label, description, ...props }) => (
  <Field>
    <span>
      {label}
       :
    </span>
    <select {...props} />
    {description}
  </Field>
)

interface CheckboxProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string
}

export const Checkbox: React.FC<CheckboxProps> = ({ label, children, ...props }) => {
  return (
    <Field>
      {label && (
        <span>
          {label}
           :
        </span>
      )}
      <label>
        <input type="checkbox" {...props} />
        <div>{children}</div>
      </label>
    </Field>
  )
}

export const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: bold;
  margin: 15px 0;
  div:first-child {
    flex: 4;
    margin-right: 30px;
  }
  span {
    font-weight: normal;
  }
  div:last-child {
    flex: 1;
    text-align: right;
    white-space: nowrap;
    margin-left: 10px;
  }
`

interface ArticleProps {
  article: IArticle
  onQuantity?: (article: IArticle, delta: number) => () => void
}
export const Article: React.FC<ArticleProps> = ({ article, onQuantity }) => {
  const { billing } = useCart()

  return (
    <Row>
      {article.type === "booking" ? (
        <div>
          {article.formula.name}
          {article.variant && (
            <>
              <br />
              <span>{article.variant}</span>
            </>
          )}
          <br />
          <span>{formatEventDate(article.date)}</span>
        </div>
      ) : (
        <div>
          Carte cadeau {article.formula.name}
          <br />
          <span>
            {article.mail ? "envoi postal de la carte" : `envoi par e-mail${billing ? ` à ${billing.email}` : ""}`}
          </span>
        </div>
      )}
      {onQuantity && <button onClick={onQuantity(article, -1)}>-</button>}
      {article.quantity}
      {onQuantity && <button onClick={onQuantity(article, 1)}>+</button>}
      <div>
        {formatMoney(article.formula.price * article.quantity)}
        {article.type === "present" ? (
          <span>
            <br />
            {formatMoney(article.mail ? MAIL_PRICE * article.quantity : 0)}
          </span>
        ) : null}
      </div>
    </Row>
  )
}

export const Separator = styled.div`
  margin: 20px 0;
  border-bottom: 1px solid ${COLORS.dark};
`

export const Bottom = styled.div`
  margin-top: 20px;
  a {
    font-weight: bold;
    text-decoration: underline;
  }
  @media (min-width: ${BREAKPOINTS.tablet}px) {
    margin-left: 90px;
  }
`

export const Button = styled.button`
  background-color: ${COLORS.main};
  color: ${COLORS.light};
  font-weight: bold;
  padding: 5px 15px;
  margin: 10px 0 20px;
  font-size: ${SIZES.normal};
  display: inline-block;
  svg {
    margin-top: -1px;
    vertical-align: -6px;
  }
  &:disabled {
    opacity: 0.5;
  }
`
